import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NxStatusBadge } from '../../../../shared/ui/status-badge/status-badge';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { CelotexStore } from '../../data-access/celotex.store';
import { Aviso } from '../../models';

@Component({
  selector: 'app-celotex-board',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatRippleModule, NxStatusBadge],
  template: `
    @if (avisos().length > 0) {
      @let total = avisos().length;
      @let aviso = avisoAtual();

      @if (aviso) {
        <article
          class="relative rounded-pmdf-card bg-pmdf-surface shadow-pmdf-card border-l-4 overflow-hidden"
          [class]="aviso.tipo === 'UNIDADE' ? 'border-pmdf-warning' : 'border-pmdf-info'"
          [attr.aria-label]="'Aviso: ' + aviso.titulo"
        >
          <div class="p-4 pr-12 flex flex-col gap-2 animate-fade-in" [attr.data-id]="aviso.id">
            <div class="flex items-center gap-2 flex-wrap">
              <nx-status-badge
                [variant]="aviso.tipo === 'UNIDADE' ? 'warning' : 'info'"
                [icon]="aviso.tipo === 'UNIDADE' ? 'groups' : 'campaign'"
                [label]="rotuloTipo(aviso.tipo)"
              />
              @if (total > 1) {
                <span class="text-[10px] text-pmdf-muted-55">
                  {{ indice() + 1 }} de {{ total }}
                </span>
              }
            </div>

            <h3 class="text-base font-semibold text-pmdf-text-primary leading-tight">
              {{ aviso.titulo }}
            </h3>

            <p class="text-sm text-pmdf-muted-70 leading-relaxed whitespace-pre-line">
              {{ aviso.descricao }}
            </p>

            <span class="flex justify-end items-center gap-1 text-[10px] text-pmdf-muted-55 mt-1">
              <mat-icon class="icon-xs">schedule</mat-icon>
              Válido até {{ formatarData(aviso.validade) }}
            </span>
          </div>

          <button
            type="button"
            matRipple
            [matRippleCentered]="true"
            [matRippleUnbounded]="true"
            class="absolute top-2 right-2 flex items-center justify-center w-9 h-9 rounded-full text-pmdf-muted-55 hover:bg-pmdf-bg-muted active:bg-pmdf-bg-muted transition-colors"
            [attr.aria-label]="'Dispensar aviso ' + aviso.titulo"
            (click)="dispensar(aviso.id)"
          >
            <mat-icon class="icon-base">close</mat-icon>
          </button>
        </article>
      }
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .animate-fade-in {
        animation: celotex-fade-in 220ms ease-out;
      }

      @keyframes celotex-fade-in {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .animate-fade-in {
          animation: none;
        }
      }
    `,
  ],
})
export class CelotexBoard {
  protected readonly store = inject(CelotexStore);
  private readonly auth = inject(AuthService);

  protected readonly avisos = computed(() => this.store.avisosVisiveis());
  protected readonly indice = signal(0);
  private readonly siglaUnidade = computed(() => this.auth.user()?.unidade?.trim() || 'Sua unidade');

  protected readonly avisoAtual = computed<Aviso | null>(() => {
    const lista = this.avisos();
    if (lista.length === 0) return null;
    const i = Math.min(this.indice(), lista.length - 1);
    return lista[i] ?? null;
  });

  constructor() {
    effect(() => {
      const total = this.avisos().length;
      if (this.indice() >= total) {
        this.indice.set(Math.max(0, total - 1));
      }
    });
  }

  protected dispensar(id: string): void {
    const indiceAtual = this.indice();
    this.store.dispensar(id);
    const novaQtd = this.avisos().length;
    if (indiceAtual >= novaQtd) {
      this.indice.set(Math.max(0, novaQtd - 1));
    }
  }

  formatarData(validade: string): string {
    const [ano, mes, dia] = validade.split('-');
    return `${dia}/${mes}/${ano}`;
  }

  protected rotuloTipo(tipo: Aviso['tipo']): string {
    return tipo === 'UNIDADE' ? this.siglaUnidade() : 'Geral';
  }
}
